import { useMemo, useState } from "react";
import Breadcrumb from "@/components/feature/Breadcrumb";
import FilterBar from "@/components/feature/FilterBar";
import NovelCard from "@/components/feature/NovelCard";
import Pagination from "@/components/feature/Pagination";
import EmptyState from "@/components/feature/EmptyState";
import { allNovels } from "@/utils/catalog";
import type { Novel } from "@/types";

const PAGE_SIZE = 6;

function toOptions(values: string[]) {
  return [{ label: "全部", value: "全部" }, ...values.map((v) => ({ label: v, value: v }))];
}

const WORD_OPTIONS = [
  { label: "全部", value: "全部" },
  { label: "50万字以下", value: "lt50" },
  { label: "50-100万字", value: "50to100" },
  { label: "100万字以上", value: "gt100" },
];

function matchWordCount(count: number, range: string) {
  if (range === "全部") return true;
  if (range === "lt50") return count < 500000;
  if (range === "50to100") return count >= 500000 && count < 1000000;
  if (range === "gt100") return count >= 1000000;
  return true;
}

export default function NovelCategoryPage() {
  const [filters, setFilters] = useState<Record<string, string>>({
    genre: "全部",
    status: "全部",
    words: "全部",
  });
  const [sort, setSort] = useState("recommend");
  const [page, setPage] = useState(1);

  const genres = useMemo(
    () => toOptions(Array.from(new Set(allNovels.map((n) => n.genre)))),
    []
  );
  const statuses = useMemo(
    () => toOptions(Array.from(new Set(allNovels.map((n) => n.status)))),
    []
  );

  const filtered = useMemo(() => {
    let list: Novel[] = allNovels.filter((n) => {
      const okGenre = filters.genre === "全部" || n.genre === filters.genre;
      const okStatus = filters.status === "全部" || n.status === filters.status;
      const okWords = matchWordCount(n.wordCount, filters.words);
      return okGenre && okStatus && okWords;
    });
    list = [...list].sort((a, b) =>
      sort === "recommend" ? b.recommend - a.recommend : Number(b.id) - Number(a.id)
    );
    return list;
  }, [filters, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const handleFilterChange = (key: string, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const resetFilters = () => {
    setFilters({ genre: "全部", status: "全部", words: "全部" });
    setSort("recommend");
    setPage(1);
  };

  return (
    <div className="w-full">
      <div className="w-full px-4 md:px-8 pt-6 pb-2">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "小说" }]} />
      </div>

      <div className="w-full px-4 md:px-8 pt-4 pb-10 md:pb-14">
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-9 h-9 rounded-lg bg-primary-500 text-background-50 flex items-center justify-center">
              <i className="ri-book-open-line text-lg"></i>
            </span>
            <h1 className="font-heading text-2xl md:text-3xl font-black text-foreground-950">
              小说分类
            </h1>
          </div>
          <p className="text-sm md:text-base text-foreground-600 max-w-2xl">
            玄幻、都市、科幻、言情、悬疑、武侠……海量小说佳作在线浏览，支持按类型、状态、字数范围筛选。
          </p>
        </div>

        <FilterBar
          groups={[
            { key: "genre", label: "类型", options: genres },
            { key: "status", label: "状态", options: statuses },
            { key: "words", label: "字数", options: WORD_OPTIONS },
          ]}
          values={filters}
          onChange={handleFilterChange}
          sorts={[
            { label: "按推荐度", value: "recommend" },
            { label: "按更新日期", value: "update" },
          ]}
          sortValue={sort}
          onSortChange={(v) => {
            setSort(v);
            setPage(1);
          }}
        />

        <div className="mt-5 mb-4 text-sm text-foreground-500">
          共找到 <span className="font-medium text-primary-600">{filtered.length}</span> 本小说作品
        </div>

        {paged.length > 0 ? (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-5">
              {paged.map((novel) => (
                <NovelCard key={novel.id} novel={novel} />
              ))}
            </div>
            <div className="mt-10">
              <Pagination current={currentPage} total={totalPages} onChange={setPage} />
            </div>
          </>
        ) : (
          <EmptyState
            title="暂无符合条件的小说内容"
            description="试试调整筛选条件，或点击下方按钮重置全部筛选。"
            actionLabel="重置筛选条件"
            onAction={resetFilters}
          />
        )}
      </div>
    </div>
  );
}